<template>
  <gmap-map ref="map" class="map" :zoom="9" :center="{ lat: -33.867001, lng:151.209113 }" />
</template>

<style scoped>
.map {
  width: 100%;
  height: 100%;
}
</style>

<script>
import { get } from 'vuex-pathify'
import { Map as GmapMap, gmapApi } from 'gmap-vue'

export default {
  props: {
    stop: Object
  },

  components: {
    GmapMap
  },

  data () {
    return {
      map: null,
      stopPosition: null,
      stopMarker: null,
      userPosition: null
    }
  },

  computed: {
    ...get('deliveryRuns', ['deliveryRun']),

    google: gmapApi,

    bounds () {
      if (!this.google) {
        return []
      }

      const bounds = new this.google.maps.LatLngBounds()

      if (this.userPosition) {
        bounds.extend(this.userPosition)
      }

      if (this.stopPosition) {
        bounds.extend(this.stopPosition)
      }

      return bounds
    }
  },

  watch: {
    bounds () {
      if (this.map && !this.bounds.isEmpty()) {
        const { north, south, east, west } = this.bounds.toJSON()

        // If the north/south and east/west coords are all the same then we only
        // have a single point within our bounds. We'll center the map and set
        // an appropriate zoom.
        if (north === south && east === west) {
          this.map.setCenter(this.bounds.getCenter())
          this.map.setZoom(15)
        } else {
          this.map.fitBounds(this.bounds, 25)
        }
      }
    },

    'stop.id' () {
      this.renderStopLocation()
    }
  },

  mounted () {
    this.$refs.map.$mapPromise.then(async (map) => {
      this.map = map

      this.renderUserLocation().then(this.renderStopLocation)
    })
  },

  methods: {
    renderStopLocation () {
      this.stopPosition = {
        lat: Number(this.stop.station.latitude),
        lng: Number(this.stop.station.longitude)
      }

      if (this.stopMarker) {
        this.stopMarker.setMap(null)
      }

      this.stopMarker = new this.google.maps.Marker({
        position: this.stopPosition
      })

      this.stopMarker.setMap(this.map)
    },

    renderUserLocation () {
      return new Promise((resolve) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            this.userPosition = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }

            const marker = new this.google.maps.Marker({
              position: this.userPosition,
              icon: {
                url: 'data:image/png;base64,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',
                scaledSize: new this.google.maps.Size(32, 32),
                anchor: new this.google.maps.Point(16, 16)
              }
            })

            marker.setMap(this.map)

            resolve()
          }, () => {
            this.$notify('error', 'Unable to retrieve your location from your device.')

            resolve()
          })
        } else {
          this.$notify('error', 'Your device does not support geolocation.')

          resolve()
        }
      })
    }
  }
}
</script>
